import 'css/src/data/transcript/nodes/TranscriptWordNode';

import classNames from 'classnames';
import type {
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    SerializedTextNode,
    Spread,
} from 'lexical';
import { TextNode } from 'lexical';

import uuid from 'js/src/libs/uuid';

export enum TranscriptWordNodeActiveState {
    DEFAULT = 'default',
    ACTIVE = 'active',
    BLURRED = 'blurred',
}
export type WordNodeType = 'word-node';
export const WordNodeTypeValue: WordNodeType = 'word-node';
export type SilenceNodeType = 'silence-node';
export const SilenceNodeTypeValue: SilenceNodeType = 'silence-node';

function serializedNodeIsV1(node: SerializedLexicalNode): node is SerializedTranscriptWordNode_v1 {
    return node.version === 1;
}

function serializedNodeIsV2(node: SerializedLexicalNode): node is SerializedTranscriptWordNode_v2 {
    return node.version === 2;
}

export type SerializedTranscriptWordNode_v1 = Spread<
    {
        startTime: number;
        endTime: number;
        type: WordNodeType | SilenceNodeType;
        className?: string;
        activeState: TranscriptWordNodeActiveState;
        version: 1;
    },
    SerializedTextNode
>;
export type SerializedTranscriptWordNode_v2 = Spread<
    {
        startTime: number;
        endTime: number;
        type: WordNodeType | SilenceNodeType;
        className?: string;
        activeState: TranscriptWordNodeActiveState;
        filler: boolean;
        version: 2;
    },
    SerializedTextNode
>;

export type SerializedTranscriptWordNode =
    | SerializedTranscriptWordNode_v1
    | SerializedTranscriptWordNode_v2;

export const EMPTY_WORD_CREATED_BY_TEXT_CORRECTION = 'empty-word-from-correction';

export type CreateTranscriptWordNodeOptions = {
    key?: NodeKey;
    id?: string;
    activeState?: TranscriptWordNodeActiveState;
    className?: string;
    isFiller?: boolean;
};

export function isSerializedTranscriptWordNode(
    node: SerializedLexicalNode,
): node is SerializedTranscriptWordNode {
    return node.type === 'word-node';
}

export class TranscriptWordNode extends TextNode {
    __startTime: number;
    __endTime: number;
    __id: string;
    __activeState?: TranscriptWordNodeActiveState;
    __className?: string;
    __isFiller: boolean;

    constructor(
        text: string,
        startTime: number,
        endTime: number,
        { key, id, activeState, className, isFiller }: CreateTranscriptWordNodeOptions = {},
    ) {
        super(text, key);
        this.__startTime = startTime;
        this.__endTime = endTime;
        if (!id) {
            id = uuid();
        }
        this.__id = id;
        this.__activeState = activeState || TranscriptWordNodeActiveState.DEFAULT;
        this.__className = className || '';
        this.__isFiller = isFiller || false;
    }

    static override getType(): string {
        return WordNodeTypeValue;
    }

    static override clone(node: TranscriptWordNode): TranscriptWordNode {
        return new TranscriptWordNode(node.__text, node.__startTime, node.__endTime, {
            key: node.__key,
            id: node.__id,
            activeState: node.__activeState,
            className: node.__className,
            isFiller: node.__isFiller,
        });
    }

    isEmptyText(): boolean {
        return !this.__text.trim();
    }

    override createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        // Although not functionally needed, the time data are useful as
        // part of the DOM element attributes for debugging errors
        element.setAttribute('startTime', this.__startTime.toFixed());
        element.setAttribute('endTime', this.__endTime.toFixed());
        element.setAttribute('wordid', this.__id);

        element.className = classNames('word-node', this.className, {
            'active-base-overlay-word-node':
                this.__activeState === TranscriptWordNodeActiveState.ACTIVE,
            'blurred-word-node': this.__activeState === TranscriptWordNodeActiveState.BLURRED,
            empty: this.isEmptyText(),
        });
        return element;
    }

    override updateDOM(
        prevNode: TranscriptWordNode,
        dom: HTMLElement,
        config: EditorConfig,
    ): boolean {
        super.updateDOM(prevNode as this, dom, config);
        dom.className = classNames('word-node', this.className, {
            'active-base-overlay-word-node':
                this.__activeState === TranscriptWordNodeActiveState.ACTIVE,
            'blurred-word-node': this.__activeState === TranscriptWordNodeActiveState.BLURRED,
            empty: this.isEmptyText(),
        });
        return false;
    }

    static override importJSON(serializedNode: SerializedTranscriptWordNode): TranscriptWordNode {
        if (serializedNodeIsV1(serializedNode)) {
            const node = $createTranscriptWordNode({
                text: serializedNode.text,
                startTime: serializedNode.startTime,
                endTime: serializedNode.endTime,
                isFiller: false, // there were no filler words in v1
            });
            node.setActiveState(serializedNode.activeState);
            node.className = serializedNode.className || '';
            return node;
        } else if (serializedNodeIsV2(serializedNode)) {
            const node = $createTranscriptWordNode({
                text: serializedNode.text,
                startTime: serializedNode.startTime,
                endTime: serializedNode.endTime,
                isFiller: serializedNode.filler,
            });
            node.setActiveState(serializedNode.activeState);
            node.className = serializedNode.className || '';
            return node;
        }
        throw new Error(
            `Unsupported TranscriptWordNode version: ${JSON.stringify(serializedNode)}`,
        );
    }

    override exportJSON(): SerializedTranscriptWordNode {
        return {
            ...super.exportJSON(),
            startTime: this.startTime,
            endTime: this.endTime,
            type: WordNodeTypeValue,
            activeState: this.activeState,
            className: this.className,
            filler: this.filler,
            version: 2,
        };
    }

    override canInsertTextBefore(): boolean {
        return true;
    }

    override canInsertTextAfter(): boolean {
        return true;
    }

    set startTime(startTime: number) {
        const self = this.getWritable();
        self.__startTime = startTime;
    }

    set endTime(endTime: number) {
        const self = this.getWritable();
        self.__endTime = endTime;
    }

    get startTime() {
        return this.__startTime;
    }

    get endTime() {
        return this.__endTime;
    }

    get filler() {
        return this.__isFiller;
    }

    get id() {
        return this.__id;
    }

    get activeState() {
        return this.__activeState || TranscriptWordNodeActiveState.DEFAULT;
    }

    setActiveState(state: TranscriptWordNodeActiveState) {
        const self = this.getWritable();
        self.__activeState = state;
        return self;
    }

    get className() {
        return this.getLatest().__className || '';
    }

    set className(className: string) {
        const self = this.getWritable();
        self.__className = className;
    }
}

export function $isTranscriptWordNode(
    node: LexicalNode | undefined | null,
): node is TranscriptWordNode {
    return node instanceof TranscriptWordNode;
}

export function $createTranscriptWordNode({
    text,
    startTime,
    endTime,
    isFiller,
    id,
}: {
    text: string;
    startTime: number;
    endTime: number;
    isFiller: boolean;
    id?: string;
}): TranscriptWordNode {
    return new TranscriptWordNode(text, startTime, endTime, {
        key: undefined,
        id,
        isFiller,
    });
}

export function $isEmptyTranscriptWordNodeCreatedByTextCorrection(
    node: LexicalNode,
): node is TranscriptWordNode {
    return $isTranscriptWordNode(node) && node.className === EMPTY_WORD_CREATED_BY_TEXT_CORRECTION;
}
